import {
  DocumentNode,
  buildClientSchema,
  visitWithTypeInfo,
  TypeInfo,
  FragmentDefinitionNode,
  GraphQLSchema,
  IntrospectionQuery,
  visit,
  GraphQLOutputType,
  SelectionSetNode,
  isObjectType,
  isInterfaceType,
  isNullableType,
  FragmentSpreadNode,
  ASTNode,
  OperationDefinitionNode,
  FieldNode
} from "graphql";
import {
  Exchange,
  Operation,
  OperationResult,
  OperationType
} from "@urql/core";
import {
  pipe,
  map,
  share,
  filter,
  merge,
  mergeMap,
  fromValue,
  takeUntil,
  fromPromise,
  Source
} from "wonka";
import { ReactSDKClient } from "@optimizely/react-sdk";
import { fragmentsUsed, getName, isAstNode } from "./graphqlHelpers";
import { CombinedError } from "urql";

/**
 * This file contains all the work needed to inspect queries, and remove nullable fields
 * that have a matched feature flag that is disabled.
 * https://onecode.polycom-labs.com/poly-glass/glass/-/wikis/Graphql/wip-and-feature-schemas
 */

/**
 * Like any good generic resolver, its a string record to map Types => Fields => Feature Flags.
 *
 * There is a type save version of this, that is generated in ui/glass/src/generated/graphql.features.ts
 * Give you all the names of types/fields that are in a feature schema.
 *
 */
export interface FeatureResolver {
  [key: string]: {
    [key: string]: string;
  };
}

export interface FeatureExchangeOpts {
  flags: FeatureResolver;
  schema: IntrospectionQuery;
  optimizely: ReactSDKClient;
  readyOpts?: Parameters<ReactSDKClient["onReady"]>[0];
}

export class AllSelectionRemovedError extends Error {
  code = "AllSelectionRemovedError";
  static isType(x: any): x is AllSelectionRemovedError {
    return x?.code === "AllSelectionRemovedError";
  }
  constructor() {
    super("All selections were removed by feature flags.");
  }
}

const supportedOperation: OperationType[] = [
  "subscription",
  "mutation",
  "query"
];
/**
 * The schange to remove fields from queries that have been turned off.
 * It is best to put this ahead of caching operations as they will inspect the query as well.
 * @param flags
 * @param schema
 * @param optimizely
 * @param readyOpts
 */
export const featureExchange = ({
  flags,
  schema,
  optimizely,
  readyOpts
}: FeatureExchangeOpts): Exchange => ({ forward }) => {
  //Turn our sql into a code based AST when we initialize.
  const clientSchema = buildClientSchema(schema);

  //Gotta waif for optimizely to be ready, if operations come through before it is, queue them up until it is ready.
  let ready: boolean = optimizely.isReady();
  const onReady = () => {
    ready = true;
  };
  const pendingReady = optimizely.onReady(readyOpts);
  pendingReady.then(onReady);

  /**
   * The method in the urql pipeline that maninpulates the query.
   * Takes on operation in, and returns the operation with feature flagged fields removed.
   * @param op
   */
  const handleFeatureFlaggedFields = (op: Operation): Operation => {
    const { kind, query, context, ...rest } = op;
    //If its not a graphql operation like query/mutation skip it.
    if (!supportedOperation.includes(kind)) {
      return op;
    }
    //Do the removal check.
    // TODO: improve performance by caching the results
    const [updatedQuery, updated] = removeFeatureFragmentsFromQuery(
      flags,
      clientSchema,
      optimizely,
      query
    );
    /**
     * Everything was removed? Send our poor empty operation on, but with a flag set on the context to indicate
     * what went wrong.
     */
    if (updatedQuery.definitions.length === 0) {
      return {
        ...op,
        context: {
          ...context,
          removeFlags: updated,
          invalidDueToRemovedFlags: true
        }
      };
    }
    return {
      ...rest,
      kind,
      context: {
        ...context,
        removeFlags: updated
      },
      query: updatedQuery
    };
  };
  return ops$ => {
    const sharedOps$ = share(ops$);
    /**
     * Setup two pipes, a teardown pipe we just pass along,
     * and a non-teardown pipe that will continue our flag based operations.
     */
    const teardownOps$ = pipe(
      sharedOps$,
      filter((operation: Operation) => {
        return operation.kind === "teardown";
      })
    );
    const pendingOps$ = pipe(
      sharedOps$,
      filter((operation: Operation) => {
        return operation.kind !== "teardown";
      })
    );
    /**
     * This is the main pipeline for dealing with the feature flags.
     * It will handle removing feature flags, and also handle the backlog queing if optimizely was not ready.
     */
    const opsWithFlaggedFields$: Source<Operation> = pipe(
      pendingOps$,
      //Preserve the original, and the modified as a tuple
      map(original => [original, handleFeatureFlaggedFields(original)]),
      mergeMap(([original, operation]) => {
        //Do nothing if we are already ready or we never could change the operation.
        // Remember, if optimizely is not ready, all flags default to OFF and any flagged field will be removed automatically.
        // If we were not ready, and fields were removed we need to hold this operation and retry when optimizely is ready.
        if (ready || !operation.context.removeFlags) {
          return fromValue(operation);
        }
        //If we need to ready, hold the operation until optimizely's ready has resolved.
        const teardown$ = pipe(
          sharedOps$,
          filter(op => {
            return op.kind === "teardown" && op.key === operation.key;
          })
        );
        return pipe(
          fromPromise(pendingReady), //When optimezly is ready, this pendingReady promise will resolve and let us retry this operation.
          map(() => handleFeatureFlaggedFields(original)),
          takeUntil(teardown$)
        );
      })
    );
    //We are going to share the results of feature flagging, this lets us use two pipes to split valid/invalid operations.
    const sharedFlagOps$ = share(opsWithFlaggedFields$);
    //For valid operations, just forward it on.
    const forwardOps$ = pipe(
      merge([sharedFlagOps$, teardownOps$]),
      filter(op => op.context?.invalidDueToRemovedFlags !== true),
      forward
    );
    //For invalid operations, we are going to resolve the URQL chain right now as invalid and prevent further exchange processing.
    const invalidOps$ = pipe(
      sharedFlagOps$,
      filter(op => op.context?.invalidDueToRemovedFlags === true), //everything was removed, operation is not good to pass through anymore
      map(operation => {
        const result: OperationResult = {
          operation,
          error: new CombinedError({
            graphQLErrors: [new AllSelectionRemovedError()]
          }),
          data: undefined
        };
        return result;
      })
    );
    return merge([invalidOps$, forwardOps$]);
  };
};

/**
 * Removing a non-optional field is not very type safe, so don't do that please.
 * @param type
 */
function mustBeNullable(type: GraphQLOutputType) {
  const isNullable = isNullableType(type);
  if (!isNullable) {
    console.warn("Cannot flag control non nullable field types.");
  }
  return isNullable;
}

function rootFragment(
  ancestors: ReadonlyArray<ASTNode | ReadonlyArray<ASTNode>>
): string | null {
  return ancestors.reduce((mostRootFragment: string | null, currentNode) => {
    if (isAstNode(currentNode) && currentNode.kind === "FragmentDefinition") {
      return getName(currentNode);
    }
    return mostRootFragment;
  }, null);
}

function findOperationFragments(root: ASTNode): Set<string> {
  const usedFragments = new Set<string>();

  visit(root, {
    OperationDefinition: (op: OperationDefinitionNode) => {
      const fragmentsToAdd = fragmentsUsed(op);
      fragmentsToAdd.forEach(f => usedFragments.add(f));
    }
  });
  return usedFragments;
}

/**
 * Find all the used Fragments in the document.
 * @param root
 */
export function findUsedFragments(root: ASTNode): Set<string> {
  //Just get the operation fragments first for simplicity, the operation is the start point of "is something used"
  const usedFragments = findOperationFragments(root);
  //Now visit all the fragments and spreads.  Order is not guaranteed, so we have to remember the dependants if we hit a
  // fragment before a spread that uses it.
  const maybeFragments: Record<string, string[]> = {};
  visit(root, {
    // eg: ...SiteForTenant
    // If there are any spreads, we need to add the name to usedFragments.
    FragmentSpread: (
      f: FragmentSpreadNode,
      key,
      parent: any,
      path,
      ancestors: ReadonlyArray<ASTNode | ReadonlyArray<ASTNode>>
    ) => {
      const fragmentSpread = getName(f);
      const root = rootFragment(ancestors);
      if (root && usedFragments.has(root)) {
        usedFragments.add(fragmentSpread);
        //If the definition was already hit, we should just add its dependant fragments too.
        // eg: The TenantSelectionFragment uses the UserSelectionFragment
        maybeFragments[fragmentSpread]?.forEach(f => usedFragments.add(f));
      }
    },
    //The defintion: eg: fragment SiteForTenant on Site { ...
    FragmentDefinition: (fragment: FragmentDefinitionNode) => {
      const name = getName(fragment);
      const usedByMe = fragmentsUsed(fragment);
      if (usedFragments.has(name)) {
        usedByMe.forEach(f => usedFragments.add(f));
      } else {
        //We are not used....yet.  Add to the maybe list so we can pull the values later.
        maybeFragments[name] = usedByMe;
      }
    }
  });
  return usedFragments;
}

/**
 * When clearedSelections completes, you may have unused fragments in the query definition.
 * Generally, that is considered an error, so we should remove unused fragments.
 * This relies on findUsedFragments, to have found the still used fragments, we just remove the rest.
 * See: featureExchange.test.ts ("removes the field fragment if the flag is off" for examples.
 * @param root
 * @param usedFragments
 */
function clearFragments(
  root: DocumentNode,
  usedFragments: Set<string>
): DocumentNode {
  return visit(root, {
    FragmentDefinition: {
      enter: (fragment: FragmentDefinitionNode) => {
        const name = getName(fragment);
        if (usedFragments.has(name)) {
          return fragment;
        }
        return null;
      }
    }
  });
}

/**
 * Use the flags information, to remove any nullable fields that have their flag off.
 *
 * We return a tuple of the updated document without the flagged fields, and a boolean to indicate if any fields were removed.
 * @param flags - The record of Types => Fields => Flag Names
 * @param typeInfo - Cached type information about our schema
 * @param optimizely - Optimizely
 * @param query - The graphql query/mutation
 */
function clearFlaggedFields(
  flags: FeatureResolver,
  typeInfo: TypeInfo,
  optimizely: ReactSDKClient,
  query: DocumentNode
): [DocumentNode, boolean] {
  let updated = false;
  //Check a feature flag, and set our updated field if we cound something to change.
  const checkFn = (flagToCheck: string) => {
    try {
      let enabled = optimizely.isFeatureEnabled(flagToCheck);
      updated = updated || !enabled;
      return enabled;
    } catch {
      return true;
    }
  };
  /** If all the fields of your set were removed, remove the parent field as well.
   eg
   tenant {
       id
       user {
         name
     }
   }
   if name from user is removed, remove user as well. resulting in
   tenant {
       id
   }
  */
  function selectionSetRemover({
    selectionSet
  }: {
    selectionSet: SelectionSetNode;
  }) {
    if (selectionSet?.selections.length === 0) {
      return null;
    }
  }

  /**
   * Our visitor, we take advantage of enter/leave behavior to minize the number of passes we need to do.
   */
  const clearedSelections = visit(
    query,
    visitWithTypeInfo(typeInfo, {
      // When we enter a selection set, we check all the fields if they are flagged, and adjust the selection set to
      // remove the fields with a feature flat that is off.
      SelectionSet: {
        enter: (selectionSet: SelectionSetNode) => {
          const allowAbleSelections = selectionSet.selections.filter(d => {
            if (d.kind === "Field") {
              const ptype = typeInfo.getParentType();
              // We are checking that our parent type explictly to be an interface or object type.
              if (isObjectType(ptype) || isInterfaceType(ptype)) {
                const typeField = ptype.getFields()[d.name.value];
                if (!typeField) return false;
                const fieldName = typeField.name;
                const parentTypeName = ptype.name;
                const flagToCheck = flags[parentTypeName]?.[fieldName];
                if (flagToCheck && mustBeNullable(typeField.type)) {
                  return checkFn(flagToCheck);
                }
              }
            }
            return true;
          });
          //Everything except the selection set, can remain the same.
          return {
            ...selectionSet,
            selections: allowAbleSelections
          };
        }
      },
      Field: {
        /**A selection set could be a field itseelf, if we have nothing selected,
         * eg:
         *   tenant {
         *     id
         *     user {
         *
         *     }
         *   }
         *
         *   User here had all its fields removed and would error, so remove it from the tenant selection.
         *   Using the leave behavior of visitor lets us automatically cascade up the selection tree, so we can do this
         *   in one pass.
         */
        leave(f: FieldNode) {
          if (f.selectionSet) {
            return selectionSetRemover({ selectionSet: f.selectionSet });
          }
          return f;
        }
      },
      OperationDefinition: {
        //Clear an operation if it has no selections
        leave: selectionSetRemover
      }
    })
  );
  return [clearedSelections, updated];
}

/**
 * We will remove feature flagged fields, then remove everything that remains unused in the query.
 * For example, its possible to remove a fragment if that field was feature flagged, in that case, remove
 * the fragment too.
 * @param flags
 * @param schema
 * @param optimizely
 * @param query
 */
function removeFeatureFragmentsFromQuery(
  flags: FeatureResolver,
  schema: GraphQLSchema,
  optimizely: ReactSDKClient,
  query: DocumentNode
): [DocumentNode, boolean] {
  //TODO: Can we pull this up and not rebuild every time?
  const typeInfo = new TypeInfo(schema);
  //Clear the selection sets, when this completes we may have unused fragments.
  // Fragments can appear in any part of the doc, so we cannot guarentee that we can find the unused fragments
  // in the same pass we remove the selections.
  const [clearedSelections, updated] = clearFlaggedFields(
    flags,
    typeInfo,
    optimizely,
    query
  );
  //No selections were removed, avoid any other visiting.
  if (!updated) {
    return [query, false];
  }
  // Any fragment not used by the query is now invalid.  This is currently limitted and only removes one level of
  // unused fragments.
  // TODO: What if the query writer is a psycho and has cascading unused fragments.
  const usedFragments = findUsedFragments(clearedSelections);
  //Remove the fragments that we did not find were still used.
  let featureFlagRemovedOperation = clearFragments(
    clearedSelections,
    usedFragments
  );
  return [featureFlagRemovedOperation, true];
}
